import { CSSProperties, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowDownRight,
  ArrowRight,
  BarChart3,
  Code2,
  Headphones,
  Infinity,
  Layers3,
  Lightbulb,
  Search,
  ShieldCheck,
  Users,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import "./ProcessShowcase.css";

const steps = [
  {
    number: "01",
    title: "Discover",
    kicker: "CLARITY STARTS HERE",
    description: "We dive deep into your goals, challenges, and opportunities to define the right strategy.",
    icon: Search,
    accent: "#1ee8ff",
  },
  {
    number: "02",
    title: "Design",
    kicker: "IDEAS INTO BLUEPRINTS",
    description: "We craft intuitive, scalable solutions with a focus on user experience and business outcomes.",
    icon: Lightbulb,
    accent: "#278dff",
  },
  {
    number: "03",
    title: "Build",
    kicker: "BRINGING IDEAS TO LIFE",
    description: "Our engineering teams build secure, high-performance products using modern technologies and AI.",
    icon: Code2,
    accent: "#8b4dff",
  },
  {
    number: "04",
    title: "Scale",
    kicker: "BUILT FOR WHAT'S NEXT",
    description: "We help you grow with scalable infrastructure, data-driven insights, and continuous optimization.",
    icon: BarChart3,
    accent: "#f34bc8",
  },
  {
    number: "05",
    title: "Support",
    kicker: "ALWAYS BY YOUR SIDE",
    description: "We provide ongoing support, monitoring, and evolution to ensure long-term success and measurable impact.",
    icon: Headphones,
    accent: "#8c5cff",
  },
];

const metrics = [
  { value: "50+", label: "Projects delivered", icon: Users },
  { value: "100%", label: "Client satisfaction", icon: ShieldCheck },
  { value: "3x", label: "Average growth boost", icon: Zap },
  { value: "Long-term", label: "Partnership focus", icon: Layers3 },
];

export default function ProcessShowcase() {
  const sectionRef = useRef<HTMLElement>(null);
  const [activeStep, setActiveStep] = useState(2);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => document.body.classList.toggle("home-process-showcase-active", entry.isIntersecting),
      { threshold: 0.35 },
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
      document.body.classList.remove("home-process-showcase-active");
    };
  }, []);

  const handlePointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - bounds.left) / bounds.width;
    const y = (event.clientY - bounds.top) / bounds.height;
    event.currentTarget.style.setProperty("--process-x", `${x * 100}%`);
    event.currentTarget.style.setProperty("--process-y", `${y * 100}%`);
    event.currentTarget.style.setProperty("--process-rx", `${(0.5 - y) * 1.5}deg`);
    event.currentTarget.style.setProperty("--process-ry", `${(x - 0.5) * 1.8}deg`);
  };

  const handlePointerLeave = (event: React.PointerEvent<HTMLElement>) => {
    event.currentTarget.style.setProperty("--process-x", "50%");
    event.currentTarget.style.setProperty("--process-y", "45%");
    event.currentTarget.style.setProperty("--process-rx", "0deg");
    event.currentTarget.style.setProperty("--process-ry", "0deg");
  };

  return (
    <section
      ref={sectionRef}
      id="process-showcase"
      data-testid="process-showcase"
      className="edx-process-showcase"
      aria-labelledby="process-showcase-heading"
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      <div className="edx-process-backdrop" aria-hidden="true">
        <img src="/edenox-process-gallery-v1.png" alt="" width="1672" height="941" loading="lazy" />
      </div>
      <div className="edx-process-spotlight" aria-hidden="true" />

      <div className="edx-process-frame">
        <header className="edx-process-header">
          <div className="edx-process-eyebrow"><i />OUR PROCESS<i /></div>
          <motion.h2
            id="process-showcase-heading"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.6 }}
          >
            <span>From idea to impact,</span>
            <span className="edx-process-gradient">with a process built for clarity.</span>
          </motion.h2>
          <p>We combine strategic thinking, technical excellence, and AI-powered innovation<br />to turn ambitious ideas into real-world results.</p>
        </header>

        <div className="edx-process-journey" role="list" aria-label="EDENOX delivery process">
          <div className="edx-process-energy-rail" aria-hidden="true"><i /><i /></div>
          {steps.map(({ number, title, kicker, description, icon: Icon, accent }, index) => (
            <div className="edx-process-step-wrap" key={title}>
              <motion.article
                role="listitem"
                tabIndex={0}
                aria-current={activeStep === index ? "step" : undefined}
                className={`edx-process-card${activeStep === index ? " is-active" : ""}`}
                style={{ "--card-accent": accent } as CSSProperties}
                onMouseEnter={() => setActiveStep(index)}
                onFocus={() => setActiveStep(index)}
                onClick={() => setActiveStep(index)}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.45 }}
                transition={{ duration: 0.48, delay: index * 0.08 }}
              >
                <span className="edx-process-number">{number}</span>
                <div className="edx-process-icon"><Icon aria-hidden="true" /></div>
                <h3>{title}</h3>
                <strong>{kicker}</strong>
                <p>{description}</p>
                <span className="edx-process-card-scan" aria-hidden="true" />
              </motion.article>
              {index < steps.length - 1 && <span className="edx-process-arrow" aria-hidden="true"><ArrowRight /></span>}
            </div>
          ))}
        </div>

        <div className="edx-process-actions">
          <Link to="/consultation" className="edx-process-primary">Start a project <ArrowDownRight /></Link>
          <a href="#process" className="edx-process-secondary">View our process <ArrowRight /></a>
        </div>

        <div className="edx-process-metrics">
          {metrics.map(({ value, label, icon: Icon }) => (
            <div key={label}>
              <Icon aria-hidden="true" />
              <span><b>{value}</b><small>{label}</small></span>
            </div>
          ))}
        </div>

        <div className="edx-process-signature" aria-hidden="true"><Infinity /> IDEAS<br />TO IMPACT<br />// EDENOX</div>
      </div>
    </section>
  );
}
